/**
 * PorcinoPage — Dashboard de Granja Porcina (Engorda)
 *
 * Muestra:
 *  - KPI cards del corral seleccionado (GDP, FCR, mortalidad%, costo/kg en pie)
 *  - Tabla de corrales activos
 *  - Historial de eventos del corral seleccionado
 *
 * Decisión P4 (2026-06-10): Vista propia /porcino separada del módulo agrícola.
 * Decisión 2026-06-10: SOLO ENGORDA — no hay cerdas madres ni camadas.
 */

import { useFieldErrors } from '../hooks/useFieldErrors';
import { useEffect, useState } from 'react';
import { useRanchoStore } from '../store/rancho.store';
import { useResetOnOpen } from '../hooks/useResetOnOpen';
import {
  Text, Spinner, Badge, Button, Field, Input, Dropdown, Option,
  makeStyles, tokens,
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCorrales,
  fetchLoteActivo,
  fetchEventos,
  createCorral,
  registrarEntrada,
  registrarPesaje,
  registrarAlimento,
  registrarMortalidad,
  registrarVenta,
  type Corral,
  type EventoPorcino,
  type PorcinoKPIs,
  type LotePorcino,
} from '../api/porcino.api';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { fetchRanchos } from '../api/rancho.api';
import { KpiCard } from '../components/shared/KpiCard';
import { useAuthStore } from '../store/auth.store';
import { TelegramHelpButton } from '../components/shared/TelegramHelpButton';
import { useT } from '../i18n';
import { NumberInput } from '../components/shared/NumberInput';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: '24px' },
  toolbar: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' },
  kpiRow:  { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  grid:    {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '24px',
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '20px',
    display:         'flex',
    flexDirection:   'column',
    gap:             '12px',
    overflowX:       'auto',
  },
  corralGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
  },
  corralCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius:    tokens.borderRadiusMedium,
    padding:         '16px',
    cursor:          'pointer',
    border:          `2px solid transparent`,
    transition:      'border-color 0.15s',
    ':hover': {
      border:          `2px solid ${tokens.colorBrandBackground}`,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  corralCardActive: {
    border:          `2px solid ${tokens.colorBrandBackground}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  empty: {
    textAlign:  'center' as const,
    padding:    '40px',
    color:      tokens.colorNeutralForeground3,
  },
});

// ─── Helpers (no hooks — receive translations as param) ───────────────────────

type PorcinoT = ReturnType<typeof useT>['porcino'];

function statusLabel(status: Corral['status'], pt: PorcinoT): string {
  const map: Record<string, string> = {
    IN_USE:   pt.statusInUse,
    ACTIVE:   pt.statusActive,
    EMPTY:    pt.statusEmpty,
    INACTIVE: pt.statusInactive,
  };
  return map[status] ?? status;
}

function statusColor(status: Corral['status']): 'success' | 'warning' | 'informative' {
  const map: Record<string, 'success' | 'warning' | 'informative'> = {
    IN_USE:   'success',
    ACTIVE:   'success',
    EMPTY:    'informative',
    INACTIVE: 'warning',
  };
  return map[status] ?? 'informative';
}

function tipoEventoLabel(tipo: string, pt: PorcinoT): string {
  const map: Record<string, string> = {
    ENTRADA:    pt.eventoEntrada,
    PESAJE:     pt.eventoPesaje,
    ALIMENTO:   pt.eventoAlimento,
    MORTALIDAD: pt.eventoMortalidad,
    MEDICACION: pt.eventoMedicacion,
    VENTA:      pt.eventoVenta,
  };
  return map[tipo] ?? tipo;
}

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function KpiSection({ kpis, lote, pt }: { kpis: PorcinoKPIs; lote: LotePorcino; pt: PorcinoT }) {
  const s          = useStyles();
  const { formatCurrency } = useLocaleSettings();
  const gdp        = kpis.gdp !== null ? `${kpis.gdp.toFixed(0)} ${pt.gdpUnit}` : '—';
  const fcr        = kpis.conversionAlimenticia !== null ? kpis.conversionAlimenticia.toFixed(2) : '—';
  const costoKg    = kpis.costoKgEnPie !== null ? formatCurrency(kpis.costoKgEnPie) : '—';
  const mortalidad = `${kpis.mortalidadPct.toFixed(1)}%`;

  return (
    <div className={s.kpiRow}>
      <KpiCard
        label={pt.kpiHeads}
        value={`${kpis.cabezasActuales} / ${kpis.cabezasIniciales}`}
        sub={`${kpis.diasEnCorral} ${pt.kpiDaysUnit}`}
      />
      <KpiCard
        label={pt.kpiWeight}
        value={`${kpis.pesoPromedioActual} kg`}
        sub={`${pt.kpiEntryPrefix}: ${lote.pesoPromedioInicial} kg`}
      />
      <KpiCard
        label={pt.kpiGdp}
        value={gdp}
        sub={pt.kpiGdpSub}
      />
      <KpiCard
        label={pt.kpiFcr}
        value={fcr}
        sub={`FCR — ${lote.kgAlimentoTotal.toFixed(0)} ${pt.kpiFcrUnit}`}
      />
      <KpiCard
        label={pt.kpiMortality}
        value={mortalidad}
        sub={`${lote.bajas} ${pt.kpiMortalityUnit}`}
      />
      <KpiCard
        label={pt.kpiCostKg}
        value={costoKg}
        sub={pt.kpiCostKgSub}
      />
    </div>
  );
}

function EventosTable({ eventos, pt, s }: { eventos: EventoPorcino[]; pt: PorcinoT; s: ReturnType<typeof useStyles> }) {
  const { formatCurrency, formatDate } = useLocaleSettings();

  /** Detalle legible de un evento, con los campos del backend (cabezasEntrada, pesoPromedioNuevo, ...). */
  const eventDetail = (ev: EventoPorcino): string => {
    const heads = ev.cabezas ?? ev.cabezasEntrada ?? ev.cabezasMuertas ?? ev.cabezasVendidas;
    const weight = ev.pesoPromedio ?? ev.pesoPromedioEntrada ?? ev.pesoPromedioNuevo;
    const parts: string[] = [];
    if (heads) parts.push(`${heads} ${pt.detailCabezas}`);
    if (weight) parts.push(`${weight} ${pt.detailKgCab}`);
    if (ev.kgAlimento) parts.push(`${ev.kgAlimento} ${pt.detailKgFood}${ev.tipoAlimento ? ` (${ev.tipoAlimento})` : ''}`);
    const cause = ev.causa ?? ev.causaMortalidad;
    if (cause) parts.push(`${pt.detailCausa}: ${cause}`);
    if (ev.medicamento) parts.push(`${ev.medicamento}${ev.dosis ? ` · ${ev.dosis}` : ''}`);
    if (ev.totalCost) parts.push(formatCurrency(ev.totalCost));
    return parts.join(' · ');
  };

  if (eventos.length === 0) {
    return <div className={s.empty}><Text size={300}>{pt.eventsEmpty}</Text></div>;
  }

  return (
    <DataTable<EventoPorcino>
      rows={[...eventos].sort((a, b) => b.fecha.localeCompare(a.fecha))}
      rowKey={(ev) => ev.eventoId}
      columns={[
        { key: 'fecha', header: pt.colDate, render: (ev) => formatDate(ev.fecha.slice(0, 10)) },
        { key: 'tipo', header: pt.colType, render: (ev) => tipoEventoLabel(ev.tipo, pt) },
        { key: 'detalle', header: pt.colDetail, width: 2, render: (ev) => eventDetail(ev) },
        { key: 'por', header: pt.colRecordedBy, render: (ev) => ev.registradoPorNombre ?? ev.registradoPor },
      ]}
    />
  );
}

// ─── NewCorralDialog ──────────────────────────────────────────────────────────

function NewCorralDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t  = useT();
  const pt = t.porcino;
  const qc = useQueryClient();

  const { data: ranchos = [] } = useQuery({ queryKey: ['ranchos'], queryFn: () => fetchRanchos() });
  const ranchoOptions = ranchos.map(r => ({ id: r.ranchoId, label: r.name }));

  const activeRanchoId = useRanchoStore((st) => st.activeRanchoId);
  const [ranchoId, setRanchoId] = useState(activeRanchoId ?? '');
  const [name, setName] = useState('');
  const [capacity, setCapacity] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const reset = () => { setRanchoId(activeRanchoId ?? ''); setName(''); setCapacity(''); setNotes(''); setError(''); fe.clear(); };
  useResetOnOpen(open, reset);

  const mut = useMutation({
    mutationFn: () => createCorral({ ranchoId: ranchoId.trim(), name: name.trim(), capacity: parseFloat(capacity) || 0, notes: notes.trim() || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['corrales'] }); reset(); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? pt.createError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      ranchoId: !ranchoId.trim() && pt.ranchoRequired,
      name: !name.trim() && pt.nameRequired,
      capacity: (!capacity || parseFloat(capacity) <= 0) && pt.capacityRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '420px' }}>
        <DialogBody>
          <DialogTitle>{pt.newCorralTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <SearchableSelect
                label={pt.fieldRanchoId}
                placeholder={pt.searchRanchoPlaceholder}
                noResultsText={t.common.searchNoResults}
                options={ranchoOptions}
                value={ranchoId}
                onChange={(id) => { setRanchoId(id); fe.clear('ranchoId'); }}
                error={fe.errors.ranchoId}
                disabled={mut.isPending}
              />
              <Field label={pt.fieldCorralName} {...fe.props('name')}>
                <Input value={name} onChange={(_, d) => { setName(d.value); fe.clear('name'); }} disabled={mut.isPending} />
              </Field>
              <Field label={pt.fieldCapacity} {...fe.props('capacity')}>
                <NumberInput value={capacity} onValueChange={(v) => { setCapacity(v); fe.clear('capacity'); }} disabled={mut.isPending} />
              </Field>
              <Field label={pt.fieldNotes}>
                <Input value={notes} onChange={(_, d) => setNotes(d.value)} disabled={mut.isPending} />
              </Field>
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button appearance="primary" onClick={handleSubmit} disabled={mut.isPending} icon={mut.isPending ? <Spinner size="tiny" /> : undefined}>
              {pt.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── NewEntradaDialog ─────────────────────────────────────────────────────────

function NewEntradaDialog({ open, onClose, corralId, ranchoId }: { open: boolean; onClose: () => void; corralId: string; ranchoId: string }) {
  const t  = useT();
  const pt = t.porcino;
  const qc = useQueryClient();

  const [cabezas, setCabezas] = useState('');
  const [pesoPromedio, setPesoPromedio] = useState('');
  const [costoPorCabeza, setCostoPorCabeza] = useState('');
  const [proveedor, setProveedor] = useState('');
  const [notas, setNotas] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const reset = () => { fe.clear(); setCabezas(''); setPesoPromedio(''); setCostoPorCabeza(''); setProveedor(''); setNotas(''); setError(''); };

  const mut = useMutation({
    mutationFn: () => registrarEntrada(corralId, {
      ranchoId,
      cabezas: parseFloat(cabezas) || 0,
      pesoPromedio: parseFloat(pesoPromedio) || 0,
      costoPorCabeza: costoPorCabeza ? parseFloat(costoPorCabeza) : undefined,
      proveedor: proveedor.trim() || undefined,
      notas: notas.trim() || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loteActivo', corralId] });
      qc.invalidateQueries({ queryKey: ['eventos', corralId] });
      qc.invalidateQueries({ queryKey: ['corrales'] });
      reset();
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? pt.createError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      cabezas: (!cabezas || parseFloat(cabezas) <= 0) && pt.cabezasRequired,
      pesoPromedio: (!pesoPromedio || parseFloat(pesoPromedio) <= 0) && pt.pesoRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '420px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="porcinoEntry"
              voiceExample={t.telegramHelp.porcinoEntryVoice}
              voiceDesc={t.telegramHelp.porcinoEntryDesc}
            />
          }>{pt.newEntradaTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                <Field label={pt.fieldCabezas} {...fe.props('cabezas')}>
                  <NumberInput value={cabezas} onValueChange={(v) => { setCabezas(v); fe.clear('cabezas'); }} disabled={mut.isPending} />
                </Field>
                <Field label={pt.fieldPesoPromedio} {...fe.props('pesoPromedio')}>
                  <NumberInput value={pesoPromedio} onValueChange={(v) => { setPesoPromedio(v); fe.clear('pesoPromedio'); }} disabled={mut.isPending} />
                </Field>
              </div>
              <Field label={pt.fieldCostoPorCabeza}>
                <NumberInput value={costoPorCabeza} onValueChange={(v) => setCostoPorCabeza(v)} disabled={mut.isPending} />
              </Field>
              <Field label={pt.fieldProveedor}>
                <Input value={proveedor} onChange={(_, d) => setProveedor(d.value)} disabled={mut.isPending} />
              </Field>
              <Field label={pt.fieldNotes}>
                <Input value={notas} onChange={(_, d) => setNotas(d.value)} disabled={mut.isPending} />
              </Field>
              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button appearance="primary" onClick={handleSubmit} disabled={mut.isPending} icon={mut.isPending ? <Spinner size="tiny" /> : undefined}>
              {pt.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── NewEventDialog ───────────────────────────────────────────────────────────

type PorcinoEventType = 'PESAJE' | 'ALIMENTO' | 'MORTALIDAD' | 'VENTA';

function NewEventDialog({ open, onClose, corralId, loteId }: { open: boolean; onClose: () => void; corralId: string; loteId: string }) {
  const t  = useT();
  const pt = t.porcino;
  const qc = useQueryClient();

  const [tipo, setTipo] = useState<PorcinoEventType>('PESAJE');
  const [pesoPromedio, setPesoPromedio] = useState('');
  const [kgAlimento, setKgAlimento] = useState('');
  const [tipoAlimento, setTipoAlimento] = useState('');
  const [cabezas, setCabezas] = useState('');
  const [causa, setCausa] = useState('');
  const [pesoTotal, setPesoTotal] = useState('');
  const [precioKg, setPrecioKg] = useState('');
  const [destino, setDestino] = useState('');
  const [notas, setNotas] = useState('');
  const [error, setError] = useState('');
  const fe = useFieldErrors();

  const reset = () => {
    fe.clear();
    setTipo('PESAJE'); setPesoPromedio(''); setKgAlimento(''); setTipoAlimento('');
    setCabezas(''); setCausa(''); setPesoTotal(''); setPrecioKg(''); setDestino(''); setNotas(''); setError('');
  };

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ['loteActivo', corralId] });
    qc.invalidateQueries({ queryKey: ['eventos', corralId] });
    qc.invalidateQueries({ queryKey: ['corrales'] });
  };

  const mut = useMutation({
    mutationFn: () => {
      if (tipo === 'PESAJE') return registrarPesaje(corralId, { loteId, pesoPromedio: parseFloat(pesoPromedio) || 0, notas: notas.trim() || undefined });
      if (tipo === 'ALIMENTO') return registrarAlimento(corralId, { loteId, kgAlimento: parseFloat(kgAlimento) || 0, tipoAlimento: tipoAlimento.trim() || undefined, notas: notas.trim() || undefined });
      if (tipo === 'MORTALIDAD') return registrarMortalidad(corralId, { loteId, cabezas: parseFloat(cabezas) || 0, causa: causa.trim() || undefined, notas: notas.trim() || undefined });
      return registrarVenta(corralId, {
        loteId, cabezas: parseFloat(cabezas) || 0, pesoTotal: parseFloat(pesoTotal) || 0,
        precioKg: parseFloat(precioKg) || 0, destino: destino.trim() || undefined, notas: notas.trim() || undefined,
      });
    },
    onSuccess: () => { invalidateAll(); reset(); onClose(); },
    onError: (e: any) => setError(e?.response?.data?.error ?? pt.createError),
  });

  const handleSubmit = () => {
    setError('');
    const ok = fe.validate({
      pesoPromedio: tipo === 'PESAJE' && (!pesoPromedio || parseFloat(pesoPromedio) <= 0) && pt.pesoRequired,
      kgAlimento: tipo === 'ALIMENTO' && (!kgAlimento || parseFloat(kgAlimento) <= 0) && pt.kgAlimentoRequired,
      cabezas: (tipo === 'MORTALIDAD' || tipo === 'VENTA') && (!cabezas || parseFloat(cabezas) <= 0) && pt.cabezasRequired,
      pesoTotal: tipo === 'VENTA' && !pesoTotal && pt.pesoTotalRequired,
      precioKg: tipo === 'VENTA' && !precioKg && pt.precioKgRequired,
    });
    if (!ok) return;
    mut.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) { reset(); onClose(); } }}>
      <DialogSurface style={{ maxWidth: '440px' }}>
        <DialogBody>
          <DialogTitle action={
            <TelegramHelpButton
              topic="porcinoEvent"
              voiceExample={t.telegramHelp.porcinoEventVoice}
              voiceDesc={t.telegramHelp.porcinoEventDesc}
            />
          }>{pt.newEventTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '8px' }}>
              <Field label={pt.fieldEventType}>
                <Dropdown
                  selectedOptions={[tipo]}
                  value={tipoEventoLabel(tipo, pt)}
                  onOptionSelect={(_, d) => { setTipo(d.optionValue as PorcinoEventType); fe.clear(); }}
                  disabled={mut.isPending}
                >
                  <Option value="PESAJE">{pt.eventoPesaje}</Option>
                  <Option value="ALIMENTO">{pt.eventoAlimento}</Option>
                  <Option value="MORTALIDAD">{pt.eventoMortalidad}</Option>
                  <Option value="VENTA">{pt.eventoVenta}</Option>
                </Dropdown>
              </Field>

              {tipo === 'PESAJE' && (
                <Field label={pt.fieldPesoPromedio} {...fe.props('pesoPromedio')}>
                  <NumberInput value={pesoPromedio} onValueChange={(v) => { setPesoPromedio(v); fe.clear('pesoPromedio'); }} disabled={mut.isPending} />
                </Field>
              )}

              {tipo === 'ALIMENTO' && (
                <>
                  <Field label={pt.fieldKgAlimento} {...fe.props('kgAlimento')}>
                    <NumberInput value={kgAlimento} onValueChange={(v) => { setKgAlimento(v); fe.clear('kgAlimento'); }} disabled={mut.isPending} />
                  </Field>
                  <Field label={pt.fieldTipoAlimento}>
                    <Input value={tipoAlimento} onChange={(_, d) => setTipoAlimento(d.value)} disabled={mut.isPending} />
                  </Field>
                </>
              )}

              {tipo === 'MORTALIDAD' && (
                <>
                  <Field label={pt.fieldCabezas} {...fe.props('cabezas')}>
                    <NumberInput value={cabezas} onValueChange={(v) => { setCabezas(v); fe.clear('cabezas'); }} disabled={mut.isPending} />
                  </Field>
                  <Field label={pt.fieldCausa}>
                    <Input value={causa} onChange={(_, d) => setCausa(d.value)} disabled={mut.isPending} />
                  </Field>
                </>
              )}

              {tipo === 'VENTA' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '12px' }}>
                    <Field label={pt.fieldCabezas} {...fe.props('cabezas')}>
                      <NumberInput value={cabezas} onValueChange={(v) => { setCabezas(v); fe.clear('cabezas'); }} disabled={mut.isPending} />
                    </Field>
                    <Field label={pt.fieldPesoTotal} {...fe.props('pesoTotal')}>
                      <NumberInput value={pesoTotal} onValueChange={(v) => { setPesoTotal(v); fe.clear('pesoTotal'); }} disabled={mut.isPending} />
                    </Field>
                  </div>
                  <Field label={pt.fieldPrecioKg} {...fe.props('precioKg')}>
                    <NumberInput value={precioKg} onValueChange={(v) => { setPrecioKg(v); fe.clear('precioKg'); }} disabled={mut.isPending} />
                  </Field>
                  <Field label={pt.fieldDestino}>
                    <Input value={destino} onChange={(_, d) => setDestino(d.value)} disabled={mut.isPending} />
                  </Field>
                </>
              )}

              <Field label={pt.fieldNotes}>
                <Input value={notas} onChange={(_, d) => setNotas(d.value)} disabled={mut.isPending} />
              </Field>

              {error && <Text style={{ color: tokens.colorStatusDangerForeground1, fontSize: '12px' }}>{error}</Text>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { reset(); onClose(); }} disabled={mut.isPending}>{t.common.cancel}</Button>
            <Button appearance="primary" onClick={handleSubmit} disabled={mut.isPending} icon={mut.isPending ? <Spinner size="tiny" /> : undefined}>
              {pt.create}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

// ─── PorcinoPage ──────────────────────────────────────────────────────────────

export function PorcinoPage() {
  const s = useStyles();
  const t = useT();
  const pt = t.porcino;
  const { user } = useAuthStore();
  const isAdmin = user?.roles.some(r => r === 'ADMIN' || r === 'SUPER_ADMIN') ?? false;
  const canWrite = user?.roles.some(r => ['FIELD_WORKER', 'SUPERVISOR', 'ADMIN', 'SUPER_ADMIN'].includes(r)) ?? false;
  const [selectedCorralId, setSelectedCorralId] = useState<string>('');
  const [newCorralOpen, setNewCorralOpen] = useState(false);
  const [newEntradaOpen, setNewEntradaOpen] = useState(false);
  const [newEventOpen, setNewEventOpen] = useState(false);

  const activeRanchoId = useRanchoStore((st) => st.activeRanchoId);
  const { data: corrales = [], isLoading: corralesLoading, isSuccess: corralesLoaded } = useQuery<Corral[]>({
    queryKey: ['corrales', activeRanchoId],
    queryFn:  () => fetchCorrales(activeRanchoId ?? undefined),
  });

  // Si el corral elegido ya no pertenece al rancho activo, reinicia la seleccion.
  useEffect(() => {
    if (selectedCorralId && corralesLoaded && !corrales.some(c => c.corralId === selectedCorralId)) {
      setSelectedCorralId('');
    }
  }, [selectedCorralId, corrales, corralesLoaded]);

  const activeCorralId = (selectedCorralId || corrales[0]?.corralId) ?? '';

  const { data: loteActivoData, isLoading: loteLoading } = useQuery({
    queryKey: ['loteActivo', activeCorralId],
    queryFn:  () => fetchLoteActivo(activeCorralId),
    enabled:  !!activeCorralId,
  });

  const { data: eventos = [], isLoading: eventosLoading } = useQuery<EventoPorcino[]>({
    queryKey: ['eventos', activeCorralId],
    queryFn:  () => fetchEventos(activeCorralId),
    enabled:  !!activeCorralId,
  });

  return (
    <div className={s.root}>
      <PageTitle>{pt.title}</PageTitle>

      {/* Selección de corral */}
      <div className={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text size={400} weight="semibold">{pt.corralTitle}</Text>
          {isAdmin && (
            <Button appearance="primary" onClick={() => setNewCorralOpen(true)}>{pt.newCorralBtn}</Button>
          )}
        </div>
        {corralesLoading ? (
          <Spinner size="small" label={pt.corralLoading} />
        ) : corrales.length === 0 ? (
          <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
            {pt.corralEmpty}
          </Text>
        ) : (
          <div className={s.corralGrid}>
            {corrales.map((c) => (
              <div
                key={c.corralId}
                className={`${s.corralCard} ${activeCorralId === c.corralId ? s.corralCardActive : ''}`}
                onClick={() => setSelectedCorralId(c.corralId)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedCorralId(c.corralId)}
              >
                <Text weight="semibold">{c.name}</Text>
                <div style={{ marginTop: '4px' }}>
                  <Badge color={statusColor(c.status)}>{statusLabel(c.status, pt)}</Badge>
                </div>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: '4px' }}>
                  {pt.corralCapLabel} {c.capacity} {pt.corralCapUnit}
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* KPIs del lote activo */}
      {activeCorralId && (
        <>
          {loteLoading ? (
            <Spinner size="small" label={pt.kpisLoading} />
          ) : loteActivoData ? (
            <>
              {canWrite && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button appearance="primary" onClick={() => setNewEventOpen(true)}>{pt.newEventBtn}</Button>
                </div>
              )}
              <KpiSection kpis={loteActivoData.kpis} lote={loteActivoData.lote} pt={pt} />
            </>
          ) : (
            <div className={s.card}>
              <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
                {pt.noActiveLot}
              </Text>
              {canWrite && (
                <Button appearance="primary" onClick={() => setNewEntradaOpen(true)} style={{ marginTop: '8px' }}>
                  {pt.newEntradaBtn}
                </Button>
              )}
            </div>
          )}

          {/* Historial de eventos */}
          <div className={s.card}>
            <Text size={400} weight="semibold">{pt.eventsTitle}</Text>
            {eventosLoading ? (
              <Spinner size="small" label={pt.eventsLoading} />
            ) : (
              <EventosTable eventos={eventos} pt={pt} s={s} />
            )}
          </div>
        </>
      )}

      <NewCorralDialog open={newCorralOpen} onClose={() => setNewCorralOpen(false)} />
      {activeCorralId && (
        <>
          <NewEntradaDialog
            open={newEntradaOpen}
            onClose={() => setNewEntradaOpen(false)}
            corralId={activeCorralId}
            ranchoId={corrales.find(c => c.corralId === activeCorralId)?.ranchoId ?? ''}
          />
          {loteActivoData && (
            <NewEventDialog
              open={newEventOpen}
              onClose={() => setNewEventOpen(false)}
              corralId={activeCorralId}
              loteId={loteActivoData.lote.loteId}
            />
          )}
        </>
      )}
    </div>
  );
}
